import React, { useState, useRef, useEffect, useCallback } from 'react';
import * as pdfjsLib from 'pdfjs-dist';

// Use same-origin worker file. Copied to dist/ via CopyWebpackPlugin.
// Cross-origin worker URLs fail in WKWebView (iOS).
pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';

interface DocumentItem {
  id: number;
  name: string;
  version_id: number;
  download_url: string | null;
}

interface DocumentViewerProps {
  document: DocumentItem;
  signatureBase64: string | null;
  signedCount: number;
  totalCount: number;
  onSign: () => void;
  onCaptureSignature: () => void;
  submitting: boolean;
  fetchDocumentUrl?: (docId: number) => Promise<string>;
}

const DocumentViewer: React.FC<DocumentViewerProps> = ({
  document: doc,
  signatureBase64,
  signedCount,
  totalCount,
  onSign,
  onCaptureSignature,
  submitting,
  fetchDocumentUrl,
}) => {
  const [numPages, setNumPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pdfLoading, setPdfLoading] = useState(true);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const [retryKey, setRetryKey] = useState(0);

  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const pdfDocRef = useRef<any>(null);
  const renderTaskRef = useRef<any>(null);

  const docNumber = signedCount + 1;
  const isLastDoc = docNumber >= totalCount;

  // Load PDF document, fetching the signed URL on-demand if missing
  useEffect(() => {
    let cancelled = false;
    setPdfLoading(true);
    setPdfError(null);
    setCurrentPage(1);
    setNumPages(0);

    async function load() {
      try {
        let url = doc.download_url;

        if (!url && fetchDocumentUrl) {
          url = await fetchDocumentUrl(doc.id);
        }

        if (!url) {
          setPdfError('No document URL available');
          setPdfLoading(false);
          return;
        }

        if (cancelled) return;

        const pdfDoc = await pdfjsLib.getDocument({
          url,
          disableAutoFetch: true,
          disableStream: true,
        }).promise;
        if (cancelled) return;
        pdfDocRef.current = pdfDoc;
        setNumPages(pdfDoc.numPages);
      } catch (err: any) {
        if (!cancelled) {
          setPdfError(`Failed to load PDF: ${err.message}`);
          setPdfLoading(false);
        }
      }
    }

    load();
    return () => {
      cancelled = true;
      if (pdfDocRef.current) {
        pdfDocRef.current.destroy();
        pdfDocRef.current = null;
      }
    };
  }, [doc.id, doc.download_url, retryKey, fetchDocumentUrl]);

  // Render current page
  const renderPage = useCallback(async () => {
    const pdfDoc = pdfDocRef.current;
    if (!pdfDoc || currentPage < 1 || currentPage > numPages) return;

    // Cancel any in-progress render to avoid "Cannot use the same canvas
    // during multiple render() operations" error
    if (renderTaskRef.current) {
      renderTaskRef.current.cancel();
      renderTaskRef.current = null;
    }

    try {
      const page = await pdfDoc.getPage(currentPage);
      const viewport = page.getViewport({ scale: 1 });

      let scale = 1;
      if (containerRef.current) {
        const containerWidth = containerRef.current.clientWidth;
        scale = containerWidth / viewport.width;
      }

      const scaledViewport = page.getViewport({ scale });
      const canvas = canvasRef.current;
      if (!canvas) return;

      const dpr = window.devicePixelRatio || 1;
      canvas.width = scaledViewport.width * dpr;
      canvas.height = scaledViewport.height * dpr;
      canvas.style.width = `${scaledViewport.width}px`;
      canvas.style.height = `${scaledViewport.height}px`;

      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      const renderTask = page.render({
        canvasContext: ctx,
        viewport: scaledViewport,
      });
      renderTaskRef.current = renderTask;

      await renderTask.promise;
      renderTaskRef.current = null;

      setPdfLoading(false);
    } catch (err: any) {
      // Ignore cancellation errors — they're expected when rapidly changing pages
      if (err.name === 'RenderingCancelledException') return;
      setPdfError(`Failed to render page: ${err.message}`);
      setPdfLoading(false);
    }
  }, [numPages, currentPage]);

  useEffect(() => {
    if (numPages > 0) {
      renderPage();
    }
  }, [numPages, currentPage, renderPage]);

  const handlePrev = () => setCurrentPage((p) => Math.max(1, p - 1));
  const handleNext = () => setCurrentPage((p) => Math.min(numPages, p + 1));

  const buttonText = submitting
    ? 'Submitting...'
    : isLastDoc
      ? 'Sign & Finish'
      : `Sign & Next (${docNumber}/${totalCount})`;

  return (
    <div
      style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
      }}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          padding: '12px 16px',
          borderBottom: '1px solid #e5e5e5',
          minHeight: '48px',
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: '16px',
              fontWeight: 700,
              color: '#1a1a2e',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {doc.name}
          </div>
        </div>
        <div style={{ textAlign: 'right', marginLeft: '12px', flexShrink: 0 }}>
          {numPages > 0 && (
            <div
              style={{ fontSize: '12px', color: '#6b7280', fontWeight: 500 }}
            >
              Page {currentPage}/{numPages}
            </div>
          )}
          <div
            style={{
              fontSize: '12px',
              color: '#6366f1',
              fontWeight: 600,
              marginTop: '2px',
            }}
          >
            Doc {docNumber} of {totalCount}
          </div>
        </div>
      </div>

      {/* PDF area */}
      <div
        ref={containerRef}
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          overflowX: 'hidden',
          backgroundColor: '#f5f5f5',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        {pdfError ? (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '40px 20px',
              minHeight: '200px',
            }}
          >
            <div style={{ fontSize: '40px', marginBottom: '12px' }}>!</div>
            <p
              style={{
                fontSize: '15px',
                color: '#6b7280',
                textAlign: 'center',
                margin: '0 0 20px',
              }}
            >
              {pdfError}
            </p>
            <button
              onClick={() => setRetryKey((k) => k + 1)}
              style={{
                padding: '12px 24px',
                borderRadius: '8px',
                border: 'none',
                backgroundColor: '#6366f1',
                color: '#ffffff',
                fontSize: '14px',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Retry
            </button>
          </div>
        ) : (
          <>
            {pdfLoading && (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '40px',
                  minHeight: '200px',
                }}
              >
                <div
                  style={{
                    width: '32px',
                    height: '32px',
                    border: '3px solid #e5e5e5',
                    borderTopColor: '#6366f1',
                    borderRadius: '50%',
                    animation: 'spin 0.8s linear infinite',
                  }}
                />
                <span
                  style={{
                    fontSize: '14px',
                    color: '#6b7280',
                    marginTop: '12px',
                  }}
                >
                  Loading document...
                </span>
                <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <canvas
                ref={canvasRef}
                style={{
                  display: pdfLoading ? 'none' : 'block',
                }}
              />
            </div>
            {/* Page navigation */}
            {numPages > 1 && !pdfLoading && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '16px',
                  padding: '12px',
                }}
              >
                <button
                  onClick={handlePrev}
                  disabled={currentPage <= 1}
                  style={{
                    padding: '10px 16px',
                    borderRadius: '8px',
                    border: '1px solid #d1d5db',
                    backgroundColor: currentPage <= 1 ? '#f3f4f6' : '#ffffff',
                    color: currentPage <= 1 ? '#9ca3af' : '#374151',
                    fontSize: '14px',
                    fontWeight: 500,
                    cursor: currentPage <= 1 ? 'default' : 'pointer',
                    minWidth: '44px',
                    minHeight: '44px',
                  }}
                >
                  Prev
                </button>
                <span style={{ fontSize: '13px', color: '#6b7280' }}>
                  {currentPage} of {numPages}
                </span>
                <button
                  onClick={handleNext}
                  disabled={currentPage >= numPages}
                  style={{
                    padding: '10px 16px',
                    borderRadius: '8px',
                    border: '1px solid #d1d5db',
                    backgroundColor:
                      currentPage >= numPages ? '#f3f4f6' : '#ffffff',
                    color: currentPage >= numPages ? '#9ca3af' : '#374151',
                    fontSize: '14px',
                    fontWeight: 500,
                    cursor: currentPage >= numPages ? 'default' : 'pointer',
                    minWidth: '44px',
                    minHeight: '44px',
                  }}
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Bottom bar */}
      <div
        style={{
          borderTop: '1px solid #e5e5e5',
          backgroundColor: '#ffffff',
          padding: '12px 16px',
          paddingBottom: 'calc(12px + env(safe-area-inset-bottom, 0px))',
        }}
      >
        {/* Signature preview - only shown when signature exists */}
        {signatureBase64 && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              padding: '8px 12px',
              backgroundColor: '#f9fafb',
              borderRadius: '10px',
              border: '1px solid #e5e7eb',
              marginBottom: '12px',
            }}
          >
            <span
              style={{
                fontSize: '13px',
                color: '#6b7280',
                fontWeight: 500,
                marginRight: '10px',
                flexShrink: 0,
              }}
            >
              Signature:
            </span>
            <div style={{ flex: 1, height: '40px' }}>
              <img
                src={signatureBase64}
                alt="Your signature"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                }}
              />
            </div>
            <button
              onClick={onCaptureSignature}
              style={{
                padding: '6px 12px',
                marginLeft: '8px',
                background: 'none',
                border: 'none',
                fontSize: '13px',
                color: '#6366f1',
                fontWeight: 600,
                cursor: 'pointer',
                flexShrink: 0,
              }}
            >
              Change
            </button>
          </div>
        )}

        {/* Action button */}
        {signatureBase64 ? (
          <button
            onClick={onSign}
            disabled={submitting || pdfLoading || !!pdfError}
            style={{
              width: '100%',
              padding: '16px',
              borderRadius: '12px',
              border: 'none',
              fontSize: '16px',
              fontWeight: 700,
              color:
                submitting || pdfLoading || pdfError ? '#9ca3af' : '#ffffff',
              backgroundColor:
                submitting || pdfLoading || pdfError ? '#e5e7eb' : '#6366f1',
              cursor:
                submitting || pdfLoading || pdfError ? 'default' : 'pointer',
              minHeight: '52px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              pointerEvents: submitting ? 'none' : 'auto',
            }}
          >
            {submitting && (
              <div
                style={{
                  width: '18px',
                  height: '18px',
                  border: '2px solid rgba(255,255,255,0.3)',
                  borderTopColor: '#ffffff',
                  borderRadius: '50%',
                  animation: 'spin 0.8s linear infinite',
                }}
              />
            )}
            {buttonText}
            <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
          </button>
        ) : (
          <button
            onClick={onCaptureSignature}
            disabled={pdfLoading || !!pdfError}
            style={{
              width: '100%',
              padding: '16px',
              borderRadius: '12px',
              border: 'none',
              fontSize: '16px',
              fontWeight: 700,
              color: pdfLoading || pdfError ? '#9ca3af' : '#ffffff',
              backgroundColor: pdfLoading || pdfError ? '#e5e7eb' : '#6366f1',
              cursor: pdfLoading || pdfError ? 'default' : 'pointer',
              minHeight: '52px',
            }}
          >
            Sign Document
          </button>
        )}
      </div>
    </div>
  );
};

export default DocumentViewer;
